import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useHistory, Link } from 'react-router-dom'
import { Flex, SecondaryHeader } from '../styles/global'
import { Paper, Button } from '@material-ui/core'
import { Input } from '../components/Input'

import { resetPassword, signOut } from '@aws-amplify/auth'
import { AUTH_USER_TOKEN_KEY } from '../utils/auth.utils'

interface UserCredentials {
  username: string;
  password: string;
}

export const ForgotPassword = () => {
  const [formData, setFormData] = useState<UserCredentials>({ username: '', password: '' })
  const [formTouched, setFormTouched] = useState<{ username: boolean, password: boolean }>({ username: false, password: false })

  const [isLoading, setLoading] = useState(false)
  const history = useHistory()

  // Clear any existing session when entering forgot password page
  useEffect(() => {
    const clearSession = async () => {
      try {
        await signOut()
      } catch (e) {
        // Ignore errors - user may not be signed in
      }
      localStorage.removeItem(AUTH_USER_TOKEN_KEY)
    }
    clearSession()
  }, [])

  const isValid = formData && formData.username !== ''

  const sendCredentials = () => {
    setLoading(true)
    resetPassword({ username: formData.username })
      .then(async (output) => {
        history.push(`/reset-password?username=${encodeURIComponent(formData.username)}`);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
        alert(err.message)
      })
  }

  return (
    <Container>
      <LoginBox elevation={1}>
        <Flex direction={'column'}>
          <SecondaryHeader>Welcome to Epidesigner</SecondaryHeader>
          <StyledInput
            id="forgot-password-email"
            label="E-Mail"
            setWidth="100%"
            type="email"
            required={formTouched.username}
            requiredString="E-Mail required."
            setValue={(value: string) => {
              setFormTouched({ ...formTouched, username: true })
              setFormData(() => {
                return { ...formData, username: value };
              });
            }}
            value={formData.username}
          />
          <Button disabled={!isValid || isLoading} onClick={() => sendCredentials()} variant="contained" color="primary">
            Reset Password
          </Button>
          <StyledLink to='/login'>
            Back to Login
          </StyledLink>
        </Flex>
      </LoginBox>
    </Container>
  )
}

const StyledInput = styled((props: any) => <Input {...props} />)`
  margin-bottom: 24px !important;
  
  .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #f7fafc;
    transition: all 0.2s ease;
    
    &:hover {
      background: #edf2f7;
    }
    
    &.Mui-focused {
      background: white;
      box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
    }
  }
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #1a202c 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(63, 81, 181, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(63, 81, 181, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`

const LoginBox = styled((props: any) => <Paper {...props} />)`
  padding: 48px;
  display: flex;
  justify-content: center;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: white;
  min-width: 400px;
  position: relative;
  z-index: 1;
  
  ${Flex} {
    width: 100%;
    
    ${StyledInput} {
      margin-bottom: 20px;
    }
    
    ${SecondaryHeader} {
      margin-bottom: 36px;
      text-align: center;
      font-size: 28px;
      font-weight: 800;
      color: #1a202c;
      letter-spacing: -0.5px;
    }
  }
  
  .MuiButton-containedPrimary {
    background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    border-radius: 10px;
    padding: 14px 24px;
    font-weight: 700;
    text-transform: none;
    font-size: 16px;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 14px rgba(63, 81, 181, 0.4);
    transition: all 0.2s ease;
    
    &:hover {
      background: linear-gradient(135deg, #303f9f 0%, #3f51b5 100%);
      box-shadow: 0 6px 20px rgba(63, 81, 181, 0.5);
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #e2e8f0;
      box-shadow: none;
      color: #a0aec0;
    }
  }
`

const StyledLink = styled((props: any) => <Link {...props} />)`
  text-align: center;
  margin-top: 24px !important;
  text-decoration: none;
  color: #3f51b5;
  font-weight: 600;
  font-size: 14px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #303f9f;
    text-decoration: underline;
  }
`
